import {ApiRaildays} from "@hps/hops-sdk-js";

// Raildays Service

const STATUS_AWARE_REQUEST = {
	validateStatus: () => true
};

class RaildaysService {

	/**
	 * Get Raildays event types.
	 *
	 * @return {Promise} Resolves with API data
	 */
	static getEventTypes() {
		return ApiRaildays.call({url: "/api/raildays/events/types"}).then(({data}) => data);
	}

	/**
	 * Get Raildays events.
	 *
	 * @param {Object} options
	 * @return {Promise} Resolves with API data
	 */
	static getEvents(options) {
		return ApiRaildays.call({
			url: "/api/raildays/events",
			params: options
		}).then(({data}) => data);
	}

	/**
	 * Get Raildays railways.
	 *
	 * @param {Object} options
	 * @return {Promise} Resolves with API data
	 */
	static getRailways(options) {
		return ApiRaildays.call({
			url: "/api/raildays/railways",
			params: options
		}).then(({data}) => data);
	}

	/**
	 * Get Raildays events for a railway.
	 *
	 * @param {Number|String} organisationId
	 * @return {Promise} Resolves with API data
	 */
	static getRailwayEvents(organisationId) {
		return ApiRaildays.call({url: "/api/raildays/events", params: {Org: organisationId}}).then(({data}) => data);
	}

	/**
	 * Get Raildays timetables for a railway.
	 *
	 * @param {Number|String} organisationId
	 * @return {Promise} Resolves with API data
	 */
	static getRailwayTimetables(organisationId) {
		return ApiRaildays.call({
			url: `/api/raildays/railways/${organisationId}/timetables/days`,
			params: {v2: "true"}
		}).then(({data}) => data);
	}

	/**
	 * Get Raildays FAQs for a railway.
	 *
	 * @param {Number|String} organisationId
	 * @return {Promise} Resolves with API data
	 */
	static getRailwayFaqs(organisationId) {
		return ApiRaildays.call({url: `/api/raildays/railways/${organisationId}/faqs`}).then(({data}) => data);
	}

	/**
	 * Get Raildays search results.
	 *
	 * @param {String} search
	 * @return {Promise} Resolves with API data
	 */
	static getSearchResults(search) {
		return ApiRaildays.call({url: "/api/raildays/search", params: {Search: search}}).then(({data}) => data);
	}

	/**
	 * Get a category's data and product list by its slug.
	 *
	 * @param {String} category
	 * @return {Promise} Resolves with API data
	 */
	static getCategoryBySlug(category) {
		return ApiRaildays.call({url: `/api/local/tickets/categories/${category}`}).then(({data}) => data);
	}

	/**
	 * Create feedback session from order details.
	 *
	 * @param {Object} params
	 * @param {String} params.orderNumber
	 * @param {String} params.postcode
	 * @param {String} params.visitDate
	 * @return {Promise<{status:Number, data:any}>}
	 */
	static createFeedback(params) {
		return ApiRaildays.call({
			...STATUS_AWARE_REQUEST,
			method: "post",
			url: "/api/raildays/feedback",
			data: params
		}).then(({status, data}) => ({status, data}));
	}

	/**
	 * Create feedback session from sales order UUID and visit date.
	 *
	 * @param {Object} params
	 * @param {String} params.salesOrderUuid
	 * @param {String} params.visitDate
	 * @return {Promise<{status:Number, data:any}>}
	 */
	static createFeedbackBySalesOrderUuid(params) {
		return ApiRaildays.call({
			...STATUS_AWARE_REQUEST,
			method: "post",
			url: "/api/raildays/feedbackBySalesOrderUuid",
			data: params
		}).then(({status, data}) => ({status, data}));
	}

	/**
	 * Get feedback questions for a feedback id.
	 *
	 * @param {Number|String} feedbackId
	 * @return {Promise<{status:Number, data:any}>}
	 */
	static getFeedbackQuestions(feedbackId) {
		return ApiRaildays.call({
			...STATUS_AWARE_REQUEST,
			url: `/api/raildays/feedback/${feedbackId}`
		}).then(({status, data}) => ({status, data}));
	}

	/**
	 * Submit feedback answers.
	 *
	 * @param {Number|String} feedbackId
	 * @param {Array<{questionId:Number|String,score:Number|null,comment:String|null}>} answers
	 * @return {Promise<{status:Number, data:any}>}
	 */
	static submitFeedbackAnswers(feedbackId, answers) {
		return ApiRaildays.call({
			...STATUS_AWARE_REQUEST,
			method: "post",
			url: `/api/raildays/feedback/${feedbackId}`,
			data: answers
		}).then(({status, data}) => ({status, data}));
	}

}

export default RaildaysService;
